import { Box, CircularProgress, Typography, alpha, keyframes } from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Pulse animation for the icon
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
`;

// Float animation for full screen
const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

interface LoadingSpinnerProps {
  fullScreen?: boolean;
  message?: string;
  size?: number;
  variant?: 'default' | 'generating' | 'flight';
}

export default function LoadingSpinner({
  fullScreen = false,
  message,
  size = 40,
  variant = 'default',
}: LoadingSpinnerProps) {
  // Full screen loading with branding
  if (fullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
          }}
        >
          {/* Animated icon */}
          <Box
            sx={{
              position: 'relative',
              animation: `${float} 3s ease-in-out infinite`,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {variant === 'flight' ? (
                <FlightTakeoffIcon sx={{ fontSize: 40, color: 'white' }} />
              ) : variant === 'generating' ? (
                <AutoAwesomeIcon sx={{ fontSize: 40, color: 'white', animation: `${pulse} 2s ease-in-out infinite` }} />
              ) : (
                <FlightTakeoffIcon sx={{ fontSize: 40, color: 'white' }} />
              )}
            </Box>
            {/* Circular progress around icon */}
            <CircularProgress
              size={100}
              thickness={2}
              sx={{
                position: 'absolute',
                top: -10,
                left: -10,
                color: 'primary.main',
                opacity: 0.3,
              }}
            />
          </Box>

          {/* Message */}
          {message && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                אנא המתינו...
              </Typography>
            </Box>
          )}

          {/* Progress dots */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  animation: `${pulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Inline spinner for cards/sections
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 4,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CircularProgress
          size={size}
          thickness={4}
          sx={{ color: 'primary.main' }}
        />
        {variant === 'generating' && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: size * 0.4, color: 'primary.main' }} />
          </Box>
        )}
      </Box>
      {message && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {message}
        </Typography>
      )}
    </Box>
  );
}
